import type { Mesh, MeshStandardMaterial, SphereGeometry } from 'three'
import * as THREE from 'three'
import { MarkerModel } from './marker'
import {
  markerMaterial_hidden,
  markerMaterial_hover,
  markerMaterial_inactive,
  markerMaterial_selected,
} from './constants'
import { setWaypointModelEventListeners, removeWaypointModelEventListeners } from './waypointEventListeners'
import { WaypointMarkerType } from '@/common/types/elements/visualizer/marker'
import { MarkerEventType } from '@/common/types/elements/visualizer/visualizerTypes'

const DEFAULT_MATERIALS = {
  hiddenMaterial: markerMaterial_hidden,
  hoverMaterial: markerMaterial_hover,
  inactiveMaterial: markerMaterial_inactive,
  selectedMaterial: markerMaterial_selected,
}

// The interface is updated to include a place to store listener references.
interface WaypointMarkerModel extends MarkerModel {
  hiddenMaterial: MeshStandardMaterial
  hoverMaterial: MeshStandardMaterial
  inactiveMaterial: MeshStandardMaterial
  selectedMaterial: MeshStandardMaterial
  marker: WaypointMarkerType
  _listeners?: Map<MarkerEventType, () => void> // ✅ Added to store listeners
}

class WaypointMarkerModel extends MarkerModel {
  constructor(marker: WaypointMarkerType, eventCallback?, interactionManager?, mesh?, options?) {
    super(marker, eventCallback, interactionManager, mesh)
    this.hiddenMaterial = options?.hiddenMaterial || DEFAULT_MATERIALS.hiddenMaterial
    this.hoverMaterial = options?.hoverMaterial || DEFAULT_MATERIALS.hoverMaterial
    this.inactiveMaterial = options?.inactiveMaterial || DEFAULT_MATERIALS.inactiveMaterial
    this.selectedMaterial = options?.selectedMaterial || DEFAULT_MATERIALS.selectedMaterial
    this.setupListeners()
  }

  setupListeners(): void {
    if (this.eventCallback && this.interactionManager) {
      setWaypointModelEventListeners(this)
      this.interactionManager.add(this.mesh)
    }
  }

  /**
   * clean up listeners and resources to prevent memory leaks.
   * This method MUST be called before the marker is discarded.
   */
  destroy(): void {
    if (this.interactionManager) {
      this.interactionManager.remove(this.mesh)
    }
    // Remove all event listeners from the mesh
    removeWaypointModelEventListeners(this)
  }

  /**
   * Creates Marker mesh and sets default properties for the mesh
   */
  createMarkerMesh(): Mesh<SphereGeometry, MeshStandardMaterial> | null {
    if (!this.marker) {
      console.error('No marker information was provided.')
      return null
    }

    if (!this.marker.meta.imageUuid) {
      console.error('No marker meta info information was provided.')
      return null
    }

    //TODO Research why pitch needs to be offset by 270 degrees
    //TODO Research why heading needs to be inverted

    // TODO Does camera angle still need to be inverted with position coming from ACI?

    const pitch = THREE.MathUtils.degToRad(270 + -this.marker.cameraangle)
    const yaw = THREE.MathUtils.degToRad(-this.marker.heading)
    const euler = new THREE.Euler(0, pitch, yaw, 'ZYX')

    const waypointGeometry = new THREE.SphereGeometry(0.2, 12, 8)
    const markerMesh = new THREE.Mesh(waypointGeometry, this.inactiveMaterial)
    const markerPosition = new THREE.Vector3(this.marker.locx, this.marker.locy, this.marker.locz)

    markerMesh.position.set(markerPosition.x, markerPosition.y, markerPosition.z)
    markerMesh.setRotationFromEuler(euler)
    markerMesh.name = this.marker.meta.imageUuid

    return markerMesh
  }

  /**
   * Sets the marker mesh material to the hidden material
   */
  setMeshToHiddenMaterial(): void {
    if (this.mesh && this.hiddenMaterial) this.mesh.material = this.hiddenMaterial
  }

  /**
   * Sets the marker mesh material to the hover material
   */
  setMeshToHoverMaterial(): void {
    if (this.mesh && this.hoverMaterial) this.mesh.material = this.hoverMaterial
  }

  /**
   * Sets the marker mesh material to the inactive material
   */
  setMeshToInactiveMaterial(): void {
    if (this.mesh && this.inactiveMaterial) this.mesh.material = this.inactiveMaterial
  }

  /**
   * Sets the marker mesh material to the selected material
   */
  setMeshToSelectedMaterial(): void {
    if (this.mesh && this.selectedMaterial) this.mesh.material = this.selectedMaterial
  }
}

export { WaypointMarkerModel }
